/**
 * React Query hooks for the worker PWA. Covers:
 *   - listing the worker's own jobs (server-side filtered by Cognito sub)
 *   - fetching one job's detail
 *   - patching status (start / complete / on_hold) — offline-queueable
 *   - uploading proof images (presigned PUT + record)
 */
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JobStatus } from '@fin/shared/enums';
import { api } from './api';
import { enqueue, isNetworkError, listQueue, remove } from './offlineQueue';

export interface JobProof {
  id: string;
  key: string;
  kind: 'before' | 'after' | 'parts' | 'signature';
  caption?: string;
  uploadedBy: string;
  uploadedAt: string;
}

export interface Job {
  id: string;
  customerId: string;
  serviceType: string;
  priority: string;
  scheduledAt: string;
  /** Booked for the day with no committed time slot. */
  anytime?: boolean;
  estimatedDurationMinutes: number;
  description?: string;
  /** Free-text notes the worker writes from the field. */
  workerNotes?: string;
  assignedWorkerId?: string;
  /** Multi-worker assignment. Primary = assignedWorkerIds[0]. */
  assignedWorkerIds?: string[];
  /** Resolved names for each id in assignedWorkerIds (single-job fetch only). */
  assignedWorkerNames?: { id: string; name: string }[];
  status: JobStatus;
  proof?: JobProof[];
  /** Worker acknowledgement of an overdue job. */
  overdueAck?: { workerId: string; at: string; etaMinutes: number; note?: string };
  /** True when the logged-in worker is assigned to this job. Workers can see
   *  every job (full team visibility) but only act on their own. */
  mine?: boolean;
}

export interface Customer {
  id: string;
  name: string;
  phone: string;
  whatsappPhone?: string;
  address: {
    building?: string;
    area: string;
    emirate: string;
    makaniNumber?: string;
  };
}

interface ListResponse<T> {
  items: T[];
}

export function useMyJobs() {
  return useQuery({
    queryKey: ['my-jobs'],
    queryFn: () => api<ListResponse<Job>>('/jobs').then((r) => r.items),
    refetchInterval: 60_000,
  });
}

export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: ['job', id],
    queryFn: () => api<Job>(`/jobs/${id}`),
    enabled: !!id,
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: () => api<Customer>(`/customers/${id}`),
    enabled: !!id,
  });
}

export function useUpdateStatus(jobId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { status: JobStatus; reason?: string }) => {
      const idempotencyKey =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      const url = `/jobs/${jobId}`;
      try {
        return await api<Job>(url, {
          method: 'PATCH',
          body: JSON.stringify(body),
          headers: { 'Idempotency-Key': idempotencyKey },
        });
      } catch (e) {
        if (jobId && isNetworkError(e)) {
          await enqueue({
            url,
            method: 'PATCH',
            body: JSON.stringify(body),
            idempotencyKey,
            label: `status: ${body.status}`,
          });
          // Optimistic local job — caller can show "queued offline" state.
          const cached = qc.getQueryData<Job>(['job', jobId]);
          return { ...(cached ?? ({} as Job)), status: body.status, _queued: true } as Job;
        }
        throw e;
      }
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['my-jobs'] });
      if (jobId) qc.setQueryData(['job', jobId], data);
    },
  });
}

/**
 * Worker saves their field notes on a job. Offline-queueable like status
 * updates so notes written without signal aren't lost.
 */
export function useUpdateWorkerNotes(jobId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (workerNotes: string) => {
      const idempotencyKey =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      const url = `/jobs/${jobId}`;
      const body = JSON.stringify({ workerNotes });
      try {
        return await api<Job>(url, {
          method: 'PATCH',
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        });
      } catch (e) {
        if (jobId && isNetworkError(e)) {
          await enqueue({ url, method: 'PATCH', body, idempotencyKey, label: 'worker notes' });
          const cached = qc.getQueryData<Job>(['job', jobId]);
          return { ...(cached ?? ({} as Job)), workerNotes, _queued: true } as Job;
        }
        throw e;
      }
    },
    onSuccess: (data) => {
      if (jobId) qc.setQueryData(['job', jobId], data);
      qc.invalidateQueries({ queryKey: ['my-jobs'] });
    },
  });
}

/**
 * Worker acknowledges an overdue job with an ETA. Optimistically writes the
 * ack into the cached job so the banner switches to "acknowledged" state
 * immediately, then refetches on success.
 */
export function useAcknowledgeOverdue(jobId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { etaMinutes: number; note?: string }) => {
      const idempotencyKey =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      return api<Job>(`/jobs/${jobId}/acknowledge-overdue`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Idempotency-Key': idempotencyKey },
      });
    },
    onSuccess: (data) => {
      if (jobId) qc.setQueryData(['job', jobId], data);
      qc.invalidateQueries({ queryKey: ['my-jobs'] });
    },
  });
}

/**
 * Replays any queued offline writes when the device reconnects. Mounted once
 * at the App root.
 */
export function useOfflineFlush() {
  const qc = useQueryClient();
  useEffect(() => {
    let running = false;
    async function flush() {
      if (running) return;
      running = true;
      try {
        const items = await listQueue();
        if (items.length === 0) return;
        for (const item of items) {
          try {
            await api(item.url, {
              method: item.method,
              body: item.body,
              headers: { 'Idempotency-Key': item.idempotencyKey },
            });
            if (item.id !== undefined) await remove(item.id);
          } catch (e) {
            if (!isNetworkError(e)) {
              // Drop poisoned items so they don't block the queue forever.
              if (item.id !== undefined) await remove(item.id);
            } else {
              // Still offline; stop and wait for next online event.
              break;
            }
          }
        }
        qc.invalidateQueries({ queryKey: ['my-jobs'] });
        qc.invalidateQueries({ queryKey: ['job'] });
      } finally {
        running = false;
      }
    }
    void flush();
    window.addEventListener('online', flush);
    return () => window.removeEventListener('online', flush);
  }, [qc]);
}

interface UploadUrlResponse {
  uploadUrl: string;
  key: string;
  proofId: string;
  kind: ProofKind;
  expiresIn: number;
}

export type ProofKind = 'before' | 'after' | 'parts' | 'video' | 'intake_photo' | 'intake_video';

export function useUploadProof(jobId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, kind, caption }: { file: File | Blob; kind: ProofKind; caption?: string }) => {
      const contentType = (file as File).type || 'image/png';
      // 1. Ask the API for a presigned PUT URL.
      const presigned = await api<UploadUrlResponse>(`/jobs/${jobId}/proof-url`, {
        method: 'POST',
        body: JSON.stringify({ contentType, kind }),
      });
      // 2. Upload the file directly to S3.
      const putRes = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status}).`);
      // 3. Record the key on the job.
      return api<JobProof>(`/jobs/${jobId}/proof`, {
        method: 'POST',
        body: JSON.stringify({ key: presigned.key, kind, caption }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job', jobId] });
    },
  });
}

// Notifications
export interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string;
  url?: string;
  createdAt: string;
  readAt?: string;
  data?: Record<string, unknown>;
}

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<ListResponse<Notification>>('/notifications').then((r) => r.items),
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api('/notifications/read', { method: 'POST', body: JSON.stringify({ ids }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

